import { c as _c } from "react/compiler-runtime";
import { type RefObject, useEffect, useState } from 'react';

/**
 * Sticky state for an element constrained by its parent container.
 * - isSticky: true when the element has reached the sticky top threshold or bottom clamp
 * - isAtBottom: true when the element's bottom is clamped to the parent's bottom
 * - isWithinBottomThreshold: true when the element's bottom is within bottomThreshold px of the parent's bottom
 */
type StickyState = {
  isSticky: boolean;
  isAtBottom: boolean;
  isWithinBottomThreshold: boolean;
};

/**
 * useRefSticky
 * Computes sticky state for a referenced element within its parent container.
 *
 * @param ref - element ref to observe
 * @param topThreshold - px distance from viewport top when considered sticky
 * @param bottomThreshold - px distance from parent's bottom considered "near bottom"
 *
 * @returns StickyState
 */
export const useRefSticky = (ref, topThreshold, bottomThreshold) => {
  const $ = _c(6);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      isSticky: false,
      isAtBottom: false,
      isWithinBottomThreshold: false
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [state, setState] = useState(t0);
  let t1;
  let t2;
  if ($[1] !== bottomThreshold || $[2] !== ref || $[3] !== topThreshold) {
    t1 = () => {
      const handleScroll = () => {
        if (!ref.current) {
          return;
        }
        const el = ref.current;
        const rect = el.getBoundingClientRect();
        const parentRect = el.parentElement?.getBoundingClientRect();
        const isAtTop = rect.top <= topThreshold;
        const isAtBottom = parentRect ? rect.bottom >= parentRect.bottom : false;
        const isWithinBottomThreshold = parentRect ? parentRect.bottom - rect.bottom <= bottomThreshold : false;
        setState({
          isSticky: isAtTop || isAtBottom,
          isAtBottom,
          isWithinBottomThreshold
        });
      };
      handleScroll();
      window.addEventListener("scroll", handleScroll, {
        passive: true
      });
      return () => window.removeEventListener("scroll", handleScroll);
    };
    t2 = [ref, topThreshold, bottomThreshold];
    $[1] = bottomThreshold;
    $[2] = ref;
    $[3] = topThreshold;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  return state;
};